/**
 * Wrap / unwrap callout tree surgery for handle-menu "Convert to section"
 * and "Remove section".
 */

import { createBlock, findBlock } from './blockUtils';
import { parentOf } from './blockTreeHelpers';
import { stripHtml } from './caretUtils';

/** True when HTML has no visible text and no element tags (pills, etc.). */
const isEffectivelyEmpty = (html) => {
  const raw = html || '';
  if (stripHtml(raw).length > 0) return false;
  return !/<[^>]+>/.test(raw);
};

/** Find the sibling list that directly contains every id (or null). */
const findSiblingList = (blocks, ids) => {
  if (!ids?.length) return null;

  const walk = (list) => {
    const indexes = ids.map((id) => list.findIndex((b) => b.id === id));
    if (indexes.every((i) => i !== -1)) {
      return { list, indexes };
    }
    for (const block of list) {
      if (block.children?.length) {
        const found = walk(block.children);
        if (found) return found;
      }
    }
    return null;
  };

  return walk(blocks);
};

/** Immutably replace a sibling sublist via a mapper on the matching list. */
const mapSiblingList = (blocks, targetList, mapFn) => {
  if (blocks === targetList) return mapFn(blocks);

  let changed = false;
  const next = blocks.map((block) => {
    if (!block.children?.length) return block;
    if (block.children === targetList) {
      changed = true;
      return { ...block, children: mapFn(block.children) };
    }
    const children = mapSiblingList(block.children, targetList, mapFn);
    if (children !== block.children) {
      changed = true;
      return { ...block, children };
    }
    return block;
  });
  return changed ? next : blocks;
};

/**
 * Replace contiguous sibling blocks with a callout whose children are those
 * blocks. Returns `{ blocks, calloutId }` or null if ids aren't siblings /
 * aren't a contiguous range.
 */
export const wrapBlocksInCallout = (blocks, ids) => {
  const uniqueIds = [...new Set(ids || [])].filter(Boolean);
  if (uniqueIds.length === 0) return null;

  const found = findSiblingList(blocks, uniqueIds);
  if (!found) return null;

  const { list, indexes } = found;
  const sorted = [...indexes].sort((a, b) => a - b);
  const start = sorted[0];
  const end = sorted[sorted.length - 1];
  // Must be a contiguous range covering exactly the requested ids
  if (sorted.length !== end - start + 1) return null;
  for (let i = start; i <= end; i += 1) {
    if (!uniqueIds.includes(list[i].id)) return null;
  }

  const children = list.slice(start, end + 1);
  const callout = createBlock('callout', '', { color: 'neutral' });
  callout.children = children;

  const next = mapSiblingList(blocks, list, (siblingList) => {
    const out = [...siblingList];
    out.splice(start, end - start + 1, callout);
    return out;
  });

  return { blocks: next, calloutId: callout.id };
};

/**
 * Lift a callout's children into its parent list and remove the callout.
 * Returns `{ blocks, focusId }` or null.
 */
export const unwrapCallout = (blocks, calloutId) => {
  const callout = findBlock(blocks, calloutId);
  if (!callout || callout.type !== 'callout') return null;

  const children = [...(callout.children || [])];
  const focusId = children[0]?.id || null;

  const parent = parentOf(blocks, calloutId);
  const siblingList = parent ? parent.children : blocks;
  if (!siblingList) return null;

  const idx = siblingList.findIndex((b) => b.id === calloutId);
  if (idx === -1) return null;

  const next = mapSiblingList(blocks, siblingList, (list) => {
    const out = [...list];
    out.splice(idx, 1, ...children);
    return out;
  });

  return { blocks: next, focusId };
};

/**
 * Split one editable block around a text selection: before / callout(mid) /
 * after. Empty before/after sides are omitted. Entire-field selection wraps
 * the original block (preserves type). Returns `{ blocks, calloutId }` or null.
 */
export const wrapTextSelectionInCallout = (blocks, blockId, { before, selected, after }) => {
  const block = findBlock(blocks, blockId);
  if (!block) return null;
  if (isEffectivelyEmpty(selected)) return null;

  // Entire field selected → wrap the block as-is (preserve type)
  if (isEffectivelyEmpty(before) && isEffectivelyEmpty(after)) {
    return wrapBlocksInCallout(blocks, [blockId]);
  }

  const mid = createBlock('text', selected || '');
  const callout = createBlock('callout', '', { color: 'neutral' });
  callout.children = [mid];

  const replacement = [];
  if (!isEffectivelyEmpty(before)) {
    replacement.push(createBlock(block.type, before || '', { ...(block.props || {}) }));
  }
  replacement.push(callout);
  if (!isEffectivelyEmpty(after)) {
    replacement.push(createBlock(block.type, after || '', { ...(block.props || {}) }));
  }

  const parent = parentOf(blocks, blockId);
  const siblingList = parent ? parent.children : blocks;
  const idx = siblingList.findIndex((b) => b.id === blockId);
  if (idx === -1) return null;

  const next = mapSiblingList(blocks, siblingList, (list) => {
    const out = [...list];
    out.splice(idx, 1, ...replacement);
    return out;
  });

  return { blocks: next, calloutId: callout.id };
};
